import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const Billing = () => {
  const navigate = useNavigate();
  const [billingData, setBillingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);
  const [resubscribeLoading, setResubscribeLoading] = useState(false);
  const [promotionalData, setPromotionalData] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  // Promotional end date: January 15, 2026
  const PROMOTIONAL_END_DATE = new Date('2026-01-15T23:59:59Z');

  useEffect(() => {
    fetchBillingData();
    fetchPromotionalData();
  }, []);

  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const diff = PROMOTIONAL_END_DATE - now;
      
      if (diff > 0) {
        setCountdown({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000),
        });
      } else {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const fetchPromotionalData = async () => {
    try {
      const response = await fetch('/billing/promotions');
      if (response.ok) {
        const data = await response.json();
        setPromotionalData(data);
      }
    } catch (err) {
      console.error('Error fetching promotional data:', err);
    }
  };

  const fetchBillingData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      // Get current user's profile with subscription info
      const profileResponse = await fetch('/auth/profile', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (profileResponse.ok) {
        const profileData = await profileResponse.json();
        setSubscription(profileData.subscription);
      }
      
      // Get current user's agency data
      const agencyResponse = await fetch('/agencies/current', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (agencyResponse.ok) {
        const agencyData = await agencyResponse.json();
        
        // Get billing plans
        const plansResponse = await fetch('/billing/plans');
        const plansData = await plansResponse.json();
        
        setBillingData({
          agency: agencyData,
          plans: plansData.plans || [],
          currentSubaccounts: agencyData.usage?.locations?.current || 0,
        });
      } else {
        setError('Failed to fetch billing data');
      }
    } catch (err) {
      console.error('Error fetching billing data:', err);
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleResubscribe = async () => {
    setResubscribeLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch('/billing/create-signup-checkout', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          successUrl: `${window.location.origin}/setup-payment?redirect_status=succeeded`,
          cancelUrl: `${window.location.origin}/dashboard/billing`,
        }),
      });
      const data = await response.json();
      if (data.success && data.clientSecret) {
        // Navigate to setup-payment which handles the embedded checkout
        navigate('/setup-payment');
      } else {
        throw new Error(data.message || 'Failed to start checkout');
      }
    } catch (err) {
      console.error('Resubscribe error:', err);
      // Fall back to setup-payment page which will handle checkout
      navigate('/setup-payment');
    } finally {
      setResubscribeLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);

    try {
      const token = localStorage.getItem('access_token');

      const response = await fetch('/billing/customer-portal', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          returnUrl: `${window.location.origin}/dashboard/billing`,
        }),
      });

      const data = await response.json();

      if (data.success && data.url) {
        const newWindow = window.open(data.url, '_blank', 'noopener,noreferrer');
        if (newWindow) {
          newWindow.focus();
        } else {
          throw new Error('Unable to open the billing portal in a new tab. Please allow popups and try again.');
        }
      } else {
        throw new Error(data.message || 'Failed to open billing portal');
      }
    } catch (error) {
      console.error('Portal error:', error);
      setError(error.message);
    } finally {
      setPortalLoading(false);
    }
  };

  const calculateCurrentCost = (subaccounts) => {
    if (!billingData?.plans) return { plan: null, cost: 0, breakdown: [], promoCost: 0, promoBreakdown: [] };
    
    let selectedPlan = billingData.plans[0]; // Default to starter
    let breakdown = [];
    let promoBreakdown = [];
    let total = 0;
    let promoTotal = 0;
    
    const isPromo = promotionalData?.isPromotionalOffer;
    
    // Regular pricing rates
    const regularRates = { tier1: 19, tier2: 15, tier3: 9 };
    // Promotional pricing rates
    const promoRates = { tier1: 15, tier2: 12, tier3: 7 };
    
    if (subaccounts > 0) {
      // Tier 1: 1-3
      const tier1Count = Math.min(subaccounts, 3);
      const tier1Cost = tier1Count * regularRates.tier1;
      const tier1PromoCost = tier1Count * promoRates.tier1;
      breakdown.push({ count: tier1Count, rate: regularRates.tier1, total: tier1Cost });
      promoBreakdown.push({ count: tier1Count, rate: promoRates.tier1, total: tier1PromoCost });
      total += tier1Cost;
      promoTotal += tier1PromoCost;
      
      // Tier 2: 4-32
      if (subaccounts > 3) {
        const tier2Count = Math.min(subaccounts - 3, 29);
        const tier2Cost = tier2Count * regularRates.tier2;
        const tier2PromoCost = tier2Count * promoRates.tier2;
        breakdown.push({ count: tier2Count, rate: regularRates.tier2, total: tier2Cost });
        promoBreakdown.push({ count: tier2Count, rate: promoRates.tier2, total: tier2PromoCost });
        total += tier2Cost;
        promoTotal += tier2PromoCost;
      }
      
      // Tier 3: 33+
      if (subaccounts > 32) {
        const tier3Count = subaccounts - 32;
        const tier3Cost = tier3Count * regularRates.tier3;
        const tier3PromoCost = tier3Count * promoRates.tier3;
        breakdown.push({ count: tier3Count, rate: regularRates.tier3, total: tier3Cost });
        promoBreakdown.push({ count: tier3Count, rate: promoRates.tier3, total: tier3PromoCost });
        total += tier3Cost;
        promoTotal += tier3PromoCost;
      }
    }
    
    // Determine plan name
    if (subaccounts <= 3) {
      selectedPlan = billingData.plans.find(p => p.id === 'starter') || billingData.plans[0];
    } else if (subaccounts <= 32) {
      selectedPlan = billingData.plans.find(p => p.id === 'growth') || billingData.plans[1];
    } else {
      selectedPlan = billingData.plans.find(p => p.id === 'infinite') || billingData.plans[2];
    }
    
    return {
      plan: selectedPlan,
      cost: isPromo ? promoTotal : total,
      regularCost: total,
      promoCost: promoTotal,
      breakdown: isPromo ? promoBreakdown : breakdown,
      regularBreakdown: breakdown,
      promoBreakdown,
      savings: total - promoTotal,
    };
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getSubscriptionStatusBadge = () => {
    if (!subscription) return null;
    
    const statusColors = {
      active: 'bg-green-100 text-green-800',
      trialing: 'bg-blue-100 text-blue-800',
      pending_payment: 'bg-yellow-100 text-yellow-800',
      past_due: 'bg-red-100 text-red-800',
      canceled: 'bg-gray-100 text-gray-800',
      inactive: 'bg-gray-100 text-gray-800',
    };
    
    const statusLabels = {
      active: 'Active',
      trialing: 'Trial',
      pending_payment: 'Pending Payment',
      past_due: 'Past Due',
      canceled: 'Canceled',
      inactive: 'Inactive',
    };
    
    const status = subscription.status || 'inactive';
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusColors[status] || statusColors.inactive}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-gray-600">Loading billing information...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  const currentBilling = calculateCurrentCost(billingData?.currentSubaccounts || 0);
  const isPromo = promotionalData?.isPromotionalOffer;
  const isSubscriptionActive = subscription?.status === 'active' || subscription?.status === 'trialing';

  return (
    <div className="space-y-6">
      {/* Promotional Banner */}
      {isPromo && (
        <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 rounded-xl shadow-lg overflow-hidden">
          <div className="px-6 py-6 sm:px-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">🎉</span>
                  <h3 className="text-xl sm:text-2xl font-bold text-white">
                    Early Bird Special - Lock in Low Prices Forever!
                  </h3>
                </div>
                <p className="text-white/90 text-sm sm:text-base">
                  Subscribe before <strong>January 15, 2026</strong> and keep promotional pricing for life! 
                  Save up to <strong>26%</strong> compared to regular pricing.
                </p>
                <div className="mt-3 flex flex-wrap gap-3 text-sm">
                  <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-white">
                    💰 $15/mo <span className="line-through opacity-60">$19</span> per subaccount (1-3)
                  </span>
                  <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-white">
                    💰 $12/mo <span className="line-through opacity-60">$15</span> per subaccount (4-32)
                  </span>
                  <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-white">
                    💰 $7/mo <span className="line-through opacity-60">$9</span> per subaccount (33+)
                  </span>
                </div>
              </div>
              
              {/* Countdown Timer */}
              <div className="flex-shrink-0">
                <div className="text-center text-white mb-2 text-sm font-medium">⏰ Offer ends in:</div>
                <div className="flex gap-2 justify-center">
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 min-w-[60px] text-center">
                    <div className="text-2xl font-bold text-white">{countdown.days}</div>
                    <div className="text-xs text-white/80">Days</div>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 min-w-[60px] text-center">
                    <div className="text-2xl font-bold text-white">{countdown.hours}</div>
                    <div className="text-xs text-white/80">Hours</div>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 min-w-[60px] text-center">
                    <div className="text-2xl font-bold text-white">{countdown.minutes}</div>
                    <div className="text-xs text-white/80">Mins</div>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 min-w-[60px] text-center">
                    <div className="text-2xl font-bold text-white">{countdown.seconds}</div>
                    <div className="text-xs text-white/80">Secs</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Billing & Usage
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage your subscription and view usage details
          </p>
        </div>
      </div>

      {/* Subscription Status Card */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Subscription Status</h3>
          {getSubscriptionStatusBadge()}
        </div>
        
        {isSubscriptionActive ? (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h4 className="text-sm font-medium text-green-800">Your subscription is active</h4>
                  <p className="mt-1 text-sm text-green-700">
                    Your billing is automatically adjusted when you add or remove subaccounts. 
                    No action needed!
                  </p>
                  {subscription?.currentPeriodEnd && (
                    <p className="mt-2 text-xs text-green-600">
                      Next billing date: {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            </div>
            
            <button
              onClick={handleManageSubscription}
              disabled={portalLoading}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {portalLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Opening...
                </>
              ) : (
                <>
                  <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Manage Subscription
                </>
              )}
            </button>
            <p className="text-xs text-gray-500 mt-2">
              Update payment method, view invoices, or cancel subscription
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {subscription?.status === 'canceled' ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-red-800">Your subscription has been canceled</h4>
                    <p className="mt-1 text-sm text-red-700">
                      Resubscribe to restore access to all features and WhatsApp integrations.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <span className="text-yellow-600 text-xl">⚠️</span>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-yellow-800">
                      Subscription Not Active
                    </h4>
                    <p className="mt-1 text-sm text-yellow-700">
                      {subscription?.status === 'pending_payment'
                        ? 'Your payment is being processed. Please wait a moment or contact support if this persists.'
                        : 'Please complete your subscription setup to access all features.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {(subscription?.status === 'canceled' || subscription?.status === 'inactive' || !subscription?.status) && subscription?.status !== 'pending_payment' && (
              <button
                onClick={handleResubscribe}
                disabled={resubscribeLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {resubscribeLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading...
                  </>
                ) : (
                  <>
                    <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {subscription?.status === 'canceled' ? 'Resubscribe' : 'Subscribe Now'}
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Current Usage Card */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Current Usage</h3>
        
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">
              {billingData?.currentSubaccounts || 0}
            </div>
            <div className="text-sm text-gray-500">Connected Subaccounts</div>
          </div>
          
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">
              {currentBilling.plan?.name || 'No Plan'}
            </div>
            <div className="text-sm text-gray-500">Current Tier</div>
          </div>
          
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600">
              {formatPrice(currentBilling.cost)}
            </div>
            <div className="text-sm text-gray-500">Monthly Cost</div>
          </div>
        </div>

        {billingData?.currentSubaccounts > 0 && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>💡 How billing works:</strong> Your subscription automatically adjusts when you add or remove subaccounts.
            </p>
            <div className="mt-2 text-sm text-blue-800">
              <div className="font-medium mb-1">Current breakdown:</div>
              {currentBilling.breakdown.map((tier, index) => (
                <div key={index} className="ml-2">
                  {tier.count} subaccount{tier.count > 1 ? 's' : ''} × ${tier.rate} = ${tier.total}
                </div>
              ))}
              <div className="mt-2 font-bold border-t border-blue-200 pt-2">
                Total: {billingData.currentSubaccounts} subaccounts = {formatPrice(currentBilling.cost)}/month
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pricing Plans */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">Pricing Tiers</h3>
          <div className="group relative">
            <span className="cursor-help text-blue-600 text-xl">ℹ️</span>
            <div className="invisible group-hover:visible absolute right-0 w-80 p-4 bg-gray-900 text-white text-sm rounded-lg shadow-lg z-10">
              <strong>Graduated pricing</strong> means you pay different rates based on the number of subaccounts:<br/><br/>
              {isPromo ? (
                <>
                  • First 3 subaccounts → $15 each<br/>
                  • 4–32 subaccounts → $12 each<br/>
                  • 33+ subaccounts → $7 each<br/><br/>
                  <strong>Example:</strong><br/>
                  4 subaccounts = (3 × $15) + (1 × $12) = $57
                </>
              ) : (
                <>
                  • First 3 subaccounts → $19 each<br/>
                  • 4–32 subaccounts → $15 each<br/>
                  • 33+ subaccounts → $9 each<br/><br/>
                  <strong>Example:</strong><br/>
                  4 subaccounts = (3 × $19) + (1 × $15) = $72
                </>
              )}
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          {/* Starter Plan */}
          <div className={`border rounded-lg p-6 relative ${
            billingData?.currentSubaccounts > 0 && billingData?.currentSubaccounts <= 3
              ? 'border-blue-500 bg-blue-50' 
              : 'border-gray-200'
          }`}>
            {billingData?.currentSubaccounts > 0 && billingData?.currentSubaccounts <= 3 && (
              <div className="absolute top-0 right-0 bg-blue-500 text-white px-3 py-1 text-xs rounded-bl-lg rounded-tr-lg">
                Current Tier
              </div>
            )}
            
            <div className="text-center">
              <h4 className="text-lg font-semibold text-gray-900">Starter</h4>
              <div className="mt-2">
                {isPromo ? (
                  <>
                    <span className="text-3xl font-bold text-green-600">$15.00</span>
                    <span className="text-gray-400 line-through ml-2">$19.00</span>
                  </>
                ) : (
                  <span className="text-3xl font-bold text-gray-900">$19.00</span>
                )}
                <div className="text-gray-500 text-sm mt-1">/subaccount/month</div>
              </div>
              
              <div className="mt-4 text-sm text-gray-600">
                <span>For 1-3 subaccounts</span>
              </div>

              <div className="mt-4">
                <div className="text-xs text-gray-500 mb-2">Examples:</div>
                <div className="text-sm text-gray-700 space-y-1 bg-gray-50 p-3 rounded">
                  <div>1 subaccount = <strong>${isPromo ? '15.00' : '19.00'}</strong></div>
                  <div>3 subaccounts = <strong>${isPromo ? '45.00' : '57.00'}</strong></div>
                </div>
              </div>
            </div>
          </div>

          {/* Growth Plan */}
          <div className={`border rounded-lg p-6 relative ${
            billingData?.currentSubaccounts >= 4 && billingData?.currentSubaccounts <= 32
              ? 'border-blue-500 bg-blue-50' 
              : 'border-gray-200'
          }`}>
            {billingData?.currentSubaccounts >= 4 && billingData?.currentSubaccounts <= 32 && (
              <div className="absolute top-0 right-0 bg-blue-500 text-white px-3 py-1 text-xs rounded-bl-lg rounded-tr-lg">
                Current Tier
              </div>
            )}
            
            <div className="text-center">
              <h4 className="text-lg font-semibold text-gray-900">Growth</h4>
              <div className="mt-2">
                {isPromo ? (
                  <>
                    <span className="text-3xl font-bold text-green-600">$12.00</span>
                    <span className="text-gray-400 line-through ml-2">$15.00</span>
                  </>
                ) : (
                  <span className="text-3xl font-bold text-gray-900">$15.00</span>
                )}
                <div className="text-gray-500 text-sm mt-1">/subaccount/month</div>
              </div>
              
              <div className="mt-4 text-sm text-gray-600">
                <span>For 4-32 subaccounts</span>
              </div>

              <div className="mt-4">
                <div className="text-xs text-gray-500 mb-2">Graduated Example:</div>
                <div className="text-xs text-gray-700 space-y-1 bg-gray-50 p-3 rounded text-left">
                  {isPromo ? (
                    <>
                      <div>First 3 subaccounts:<br/><span className="ml-2">3 × $15 = <strong>$45</strong></span></div>
                      <div>4th subaccount:<br/><span className="ml-2">1 × $12 = <strong>$12</strong></span></div>
                      <div className="border-t pt-1 mt-1">Total for 4 = <strong>$57</strong></div>
                    </>
                  ) : (
                    <>
                      <div>First 3 subaccounts:<br/><span className="ml-2">3 × $19 = <strong>$57</strong></span></div>
                      <div>4th subaccount:<br/><span className="ml-2">1 × $15 = <strong>$15</strong></span></div>
                      <div className="border-t pt-1 mt-1">Total for 4 = <strong>$72</strong></div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Infinite Plan */}
          <div className={`border rounded-lg p-6 relative ${
            billingData?.currentSubaccounts >= 33
              ? 'border-blue-500 bg-blue-50' 
              : 'border-gray-200'
          }`}>
            {billingData?.currentSubaccounts >= 33 && (
              <div className="absolute top-0 right-0 bg-blue-500 text-white px-3 py-1 text-xs rounded-bl-lg rounded-tr-lg">
                Current Tier
              </div>
            )}
            
            <div className="text-center">
              <h4 className="text-lg font-semibold text-gray-900">Infinite</h4>
              <div className="mt-2">
                {isPromo ? (
                  <>
                    <span className="text-3xl font-bold text-green-600">$7.00</span>
                    <span className="text-gray-400 line-through ml-2">$9.00</span>
                  </>
                ) : (
                  <span className="text-3xl font-bold text-gray-900">$9.00</span>
                )}
                <div className="text-gray-500 text-sm mt-1">/subaccount/month</div>
              </div>
              
              <div className="mt-4 text-sm text-gray-600">
                <span className="text-green-600 font-medium">For 33+ subaccounts</span>
              </div>

              <div className="mt-4">
                <div className="text-xs text-gray-500 mb-2">Graduated Example:</div>
                <div className="text-xs text-gray-700 space-y-1 bg-gray-50 p-3 rounded text-left">
                  {isPromo ? (
                    <>
                      <div>First 3: 3 × $15 = <strong>$45</strong></div>
                      <div>Next 29: 29 × $12 = <strong>$348</strong></div>
                      <div>33rd: 1 × $7 = <strong>$7</strong></div>
                      <div className="border-t pt-1 mt-1">Total for 33 = <strong>$400</strong></div>
                    </>
                  ) : (
                    <>
                      <div>First 3: 3 × $19 = <strong>$57</strong></div>
                      <div>Next 29: 29 × $15 = <strong>$435</strong></div>
                      <div>33rd: 1 × $9 = <strong>$9</strong></div>
                      <div className="border-t pt-1 mt-1">Total for 33 = <strong>$501</strong></div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-2">✨ Graduated Pricing Model {isPromo && <span className="text-green-600">(Promotional Rates)</span>}</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Billing automatically adjusts when you add or remove subaccounts</li>
            {isPromo ? (
              <>
                <li>• <strong>1-3 subaccounts:</strong> <span className="text-green-600">$15.00</span> <span className="line-through text-gray-400">$19.00</span> each</li>
                <li>• <strong>4-32 subaccounts:</strong> <span className="text-green-600">$12.00</span> <span className="line-through text-gray-400">$15.00</span> each</li>
                <li>• <strong>33+ subaccounts:</strong> <span className="text-green-600">$7.00</span> <span className="line-through text-gray-400">$9.00</span> each</li>
              </>
            ) : (
              <>
                <li>• <strong>1-3 subaccounts:</strong> $19.00 each</li>
                <li>• <strong>4-32 subaccounts:</strong> $15.00 each</li>
                <li>• <strong>33+ subaccounts:</strong> $9.00 each</li>
              </>
            )}
            <li>• No setup fees or hidden charges</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Billing;
